import { CoachRow } from './tabData';
import { checkSample } from './coachAnalysis';

/**
 * 강사 사분면 — 판정과 문구.
 *
 * ─────────────────────────────────────────────────────────────
 * 왜 이 파일이 따로 있나
 * ─────────────────────────────────────────────────────────────
 * 원래 이 내용은 `OpsBriefingCoachesPage.tsx` 안의 로컬 상수였습니다.
 * 강사 데이터 드로어(CoachDataDrawer)가 같은 '유형'을 보여줘야 하는데,
 * 거기서 다시 적으면 **같은 강사가 화면마다 다른 유형으로 불립니다.**
 * 예전에 옛 CoachDetailDrawer 를 지운 이유가 정확히 그것이었습니다 —
 * 리포트와 다른 데이터를 먹어 숫자가 어긋났습니다. 판정은 한 곳에만 둡니다.
 *
 * 이 파일은 **계산을 새로 만들지 않습니다.** 강사 현황이 쓰던 식을 옮겨 온 것뿐이고,
 * 표본 하한은 여전히 `coachAnalysis.checkSample` 한 곳에서 옵니다.
 */

export type QuadrantKey = 'STAR' | 'SALES' | 'CARE' | 'RISK';

export const QUADRANTS = [
    {
        key: 'STAR',
        label: '핵심 인재',
        desc: '전환도 일지도 높음',
        howTo: '대우와 일정 여유를 지켜주세요. 이 강사의 방식을 팀에 퍼뜨리면 센터 전체가 올라갑니다.',
        color: '#00795F',
        bg: 'rgba(0,167,147,0.07)',
    },
    {
        key: 'SALES',
        label: '세일즈형',
        desc: '전환은 좋지만 관리가 성김',
        howTo: '일지·응답은 알림과 기준으로 메울 수 있습니다. 사람을 바꾸려 하지 말고 시스템으로 보완하세요.',
        color: '#3D73DD',
        bg: 'rgba(61,115,221,0.07)',
    },
    {
        key: 'CARE',
        label: '관리형',
        desc: '성실하지만 전환이 낮음',
        howTo: '가장 아까운 유형입니다. 성실함은 이미 갖췄으니 재등록 이야기 꺼내는 법만 가르치면 됩니다.',
        color: '#B45309',
        bg: 'rgba(240,160,32,0.08)',
    },
    {
        key: 'RISK',
        label: '집중 관리',
        desc: '두 축 모두 낮음',
        howTo: '문책보다 원인 파악이 먼저입니다. 과부하인지, 배정 문제인지, 의지 문제인지 확인하세요.',
        color: '#C22D38',
        bg: 'rgba(255,62,74,0.07)',
    },
] as const;

/*
 * 세로축 — '관리력'.
 *
 * 원래 diligenceScore(성실도 종합)를 썼는데, 서버가 그 값을 **늘 null 로**
 * 보냅니다(집계 경로 없음). `?? 0` / `?? 50` 으로 뭉개고 있어서 실제로는
 *   · 모든 점이 세로 한가운데 y=50 에 겹치고
 *   · '핵심 인재'와 '케어형' 칸이 영원히 비어 있었습니다.
 * 목 데이터에만 값이 있어 화면에서는 멀쩡해 보였습니다.
 *
 * 라포가 실제로 재는 값 중 관리력에 가장 가까운 것은 수업일지 작성률입니다.
 * 축 이름도 그대로 바꿉니다 — 재지 않는 것의 이름을 붙여 두면
 * 원장이 없는 지표로 강사를 평가하게 됩니다.
 */
export const careScoreOf = (c: CoachRow): number | null => c.journalPct;
export const CARE_AXIS_LABEL = '일지 작성률';

/**
 * 사분면 판정.
 * 가로 기준선은 반드시 그 센터의 실제 평균이어야 합니다.
 * 상수로 박아두면 센터마다 다른 수준을 같은 자로 재게 됩니다.
 */
export const quadrantOf = (
    c: CoachRow,
    convMid: number | null,
    careMid: number | null
): QuadrantKey | null => {
    // 기준선이 없으면(아직 아무도 값이 없음) 사분면을 나눌 수 없다.
    // 0 을 기준으로 삼으면 전원이 '상위'로 잡힌다.
    if (convMid == null || careMid == null) return null;
    /*
     * 표본이 없어 못 잰 강사는 사분면에 넣지 않는다.
     * ?? 0 으로 뭉개면 "아직 못 쟀다"가 "최악"이 되어,
     * 신규 강사가 전부 '위험'으로 분류된다.
     */
    if (c.conversionPct == null || careScoreOf(c) == null) return null;
    /*
     * 표본이 모자란 강사도 넣지 않는다.
     *
     * 담당 1명·수업 3회면 전환율 100%·일지 100%가 나온다. 두 축 모두 만점이라
     * 점이 '핵심 인재' 칸 오른쪽 위 끝에 박히는데, 그건 실력이 아니라
     * 표본 크기의 부작용이다. 원장은 그 점을 보고 그 강사를 기준으로 삼는다.
     * (같은 기준을 등급에도 쓴다 — coachAnalysis.checkSample)
     */
    if (!checkSample(c).enough) return null;
    const hiConv = c.conversionPct >= convMid;
    const hiCare = (careScoreOf(c) as number) >= careMid;
    if (hiConv && hiCare) return 'STAR';
    if (hiConv) return 'SALES';
    if (hiCare) return 'CARE';
    return 'RISK';
};

/** 사분면 키 → 문구. 못 놓은 강사(null)는 유형 자체가 없다 */
export const quadrantMeta = (key: QuadrantKey | null) =>
    key ? (QUADRANTS.find((q) => q.key === key) ?? null) : null;
